import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { AlertCircle, Loader2 } from 'lucide-react';
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import './policy-reader.css';

// Disable PDF.js worker to avoid CORS issues in development
// The library will fall back to synchronous processing
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  fileUrl?: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  onTotalPagesChange: (total: number) => void;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  fileUrl,
  currentPage,
  onPageChange,
  onTotalPagesChange
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate responsive page width
  useEffect(() => {
    const updatePageWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        // Leave some padding and ensure it fits mobile screens
        const maxWidth = Math.min(containerWidth - 32, 800);
        setPageWidth(maxWidth);
      }
    };

    updatePageWidth();
    window.addEventListener('resize', updatePageWidth);
    return () => window.removeEventListener('resize', updatePageWidth);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    onTotalPagesChange(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF loading error:', error);
    setError('Failed to load PDF document');
    setLoading(false);
  };

  const onPageLoadSuccess = () => {
    setLoading(false);
  };

  const onPageLoadError = (error: Error) => {
    console.error('PDF page loading error:', error);
    setError('Failed to load PDF page');
    setLoading(false);
  };

  if (!fileUrl) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No PDF file available</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full">
      <div className="flex flex-col items-center bg-gray-50 dark:bg-gray-900 min-h-[400px] p-4">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
              <p className="text-muted-foreground">Loading PDF...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
              <p className="text-destructive">{error}</p>
            </div>
          </div>
        )}

        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
          error=""
          className="max-w-full"
        >
          <Page
            pageNumber={currentPage}
            width={pageWidth}
            onLoadSuccess={onPageLoadSuccess}
            onLoadError={onPageLoadError}
            loading=""
            error=""
            className="shadow-lg border border-gray-200 dark:border-gray-700 bg-white"
            renderTextLayer={true}
            renderAnnotationLayer={true}
          />
        </Document>

        {/*{numPages > 0 && (*/}
        {/*  <div className="mt-4 text-sm text-muted-foreground">*/}
        {/*    Page {currentPage} of {numPages}*/}
        {/*  </div>*/}
        {/*)}*/}
      </div>
    </div>
  );
};
